import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { entityBrowserActions, createRootEntity } from '../../redux/actions';
import { Button, Space } from 'antd';
import { MenuUnfoldOutlined, MenuFoldOutlined } from '@ant-design/icons';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import { Button as MatButton } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Popover from '@mui/material/Popover';
import Input from '@mui/material/Input';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import './styles.scss';
import { getScenarioId } from '../../utils/scenarioIds';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


// ---------- helpers ----------

// basically just to ensure stable keys that won't break if backend changes. 
// Relies on scenario_id and entity_id being unique across the tree, which they should be.

const makeNodeId = (n) => (n.scenario_id ? `s:${n.scenario_id}` : `e:${n.entity_id}`); 

const bySortOrder = (a, b) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
  Number(a.scenario_id ?? a.entity_id ?? 0) - Number(b.scenario_id ?? b.entity_id ?? 0);


function createTreeData(entities) {
  return (entities || []).map((entity) => {
    // Business case leaf
    if (entity.business_case_id) {
      return {
        ...entity,
        key: `bc:${entity.business_case_id}`, // was: entity.key (backend-supplied, unreliable)
        children: [],
      };
    }

    // Scenario leaf
    if (entity.scenario_id != null) {
      return {
        ...entity,
        isScenario: true,
        key: `s:${entity.scenario_id}`, // was: entity.key
        children: [],
      };
    }

    // Entity node
   const scenarioChildren = [...(entity.scenario || [])]
      .sort(bySortOrder)
      .map((s) => ({ ...s, isScenario: true, key: `s:${s.scenario_id}`, children: [] }));

    const childEntities = entity.children
      ? createTreeData([...entity.children].sort(bySortOrder))
      : [];

    return {
      ...entity,
      key: `e:${entity.entity_id}`, // was: entity.key
      children: [...scenarioChildren, ...childEntities],
    };
  });
}

function SortableNode({ nodeId, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: nodeId,
    disabled,
  });

  return children({
    setNodeRef,
    style: {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.4 : 1,
    },
    handleProps: { ...attributes, ...listeners },
  });
}

const stopPropagation = (e) => e.stopPropagation();

// ---------- component ----------

const EntitiesBrowser = ({
  entityStructure,
  handleSelection,
  handleNewScenario,
  handleNewEntity,
  handleDelete,
  handleNameChange,
  handleCheck,
  expandedBusinessCases,
  setExpandedBusinessCases,
  editable,
  checkedNodes = [],
  radioButtonMode,
  highlightedScenarios: highlightedScenariosProp,
  setHighlightedScenarios: setHighlightedScenariosProp,
  lastClickedEntity: lastClickedEntityProp,
  setLastClickedEntity: setLastClickedEntityProp,
  entitiesVersion = 0,
  handleReorderEntities,
  handleReorderScenarios,
  reorderable = true,

}) => {
  const dispatch = useDispatch();
  const expandedNodes = useSelector((state) => state.entityBrowser.expandedNodeIds);
  const fetchingExpandedNodeIds = useSelector((state) => state.entityBrowser.fetchingExpandedNodeIds);
  const selectedNodeIds = useSelector((s) => s.entityBrowser.selectedNodeIds);

  const treeData = useMemo(
    () => createTreeData(entityStructure),
    [entityStructure],
  );

  const [editingEntity, setEditingEntity] = useState({});
  const [editingName, setEditingName] = useState('');

  const [delAnchorEl, setDelAnchorEl] = useState(null);
  const [deleteNode, setDeleteNode] = useState(null);
  const openDelPopOver = Boolean(delAnchorEl);

  // Local fallback for highlightedScenarios / lastClickedEntity
  const [highlightedScenariosLocal, setHighlightedScenariosLocal] = useState([]);
  const [lastClickedEntityLocal, setLastClickedEntityLocal] = useState(null);

  const highlightedScenarios = highlightedScenariosProp ?? highlightedScenariosLocal;
  const setHighlightedScenarios = setHighlightedScenariosProp ?? setHighlightedScenariosLocal;

  const lastClickedEntity = lastClickedEntityProp ?? lastClickedEntityLocal;
  const setLastClickedEntity = setLastClickedEntityProp ?? setLastClickedEntityLocal;

  const fontMuiTreeView = useMemo(
    () =>
      createTheme({
        typography: {
          fontSize: 15,
          fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
            '"Noto Color Emoji"',
          ].join(','),
        },
        components: {
          MuiTreeItem: {
            styleOverrides: {
              content: {
                '&:hover': { backgroundColor: alpha('#1890ff', 0.18) },
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: alpha('#1890ff', 0.18),
                },
                '&.Mui-focused, &.Mui-focused:hover': {
                  backgroundColor: alpha('#1890ff', 0.18),
                },
              },
            },
          },
        },
      }),
    [],
  );

  const onExpand = (e, updatedExpandedNodeIds) => {
    if (editable) {
      dispatch(entityBrowserActions.updateExpandedNodeIds(updatedExpandedNodeIds));
    } else if (setExpandedBusinessCases) {
      setExpandedBusinessCases(updatedExpandedNodeIds);
    }
  };

  const keyOf = (n) => n?.key ?? getScenarioId(n);

  const { nodeIndex, parentIndex } = useMemo(() => {
    const map = new Map();
    const parents = new Map();
    const walk = (n, parentNodeId) => {
      const id = makeNodeId(n);
      map.set(id, n);
      parents.set(id, parentNodeId);
      (n.children || []).forEach((c) => walk(c, id));
    };
    (treeData || []).forEach((n) => walk(n, null));
    return { nodeIndex: map, parentIndex: parents };
  }, [treeData]);

  const handleStartEdit = (nodeId, name) => {
    setEditingEntity({ [nodeId]: true });
    setEditingName(name || '');
  };

  const handleStopEdit = (nodeId) => {
    // no nodeId → clear all edit state
    if (!nodeId) {
      setEditingEntity({});
      setEditingName('');
      return;
    }

    setEditingEntity((prev) => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
    setEditingName('');
  };

  const handleEnterPress = (event, node, nodeId) => {
    if (!node) return;
    if (!editingEntity[nodeId]) return;

    if (event.key === 'Enter') {
      if (handleNameChange && editingName !== node.name) {
        handleNameChange(node, editingName);
      }
      handleStopEdit(nodeId);
    } else if (event.key === 'Escape' || event.key === 'Esc') {
      handleStopEdit(nodeId);
    }
  };

  const handleOpenDeletePopup = (e) => {
    setDelAnchorEl(e.currentTarget);
  };

  const handleCloseDeletePopup = (e) => {
    if (e) stopPropagation(e);
    setDelAnchorEl(null);
  };

 const handleToggle = (e, nodeIds) => {
  const clickedInsideInput = e.target.closest('input');
  if (clickedInsideInput) return;
  onExpand(e, nodeIds);
};

const getNodeByIdFromIndex = (scenarioId) => nodeIndex.get(`s:${scenarioId}`);

 const collectWithDescendants = (node, out) => {
    if (!node) return;
    out.push(node);
    if (Array.isArray(node.children)) {
      node.children.forEach((child) => collectWithDescendants(child, out));
    }
  };

  const onCheckChange = (_tree, checked, scenarioId) => {
    const collected = [];
    const start = getNodeByIdFromIndex(scenarioId);
    if (start) collectWithDescendants(start, collected);

    if (radioButtonMode) {
      handleCheck?.(checked && start ? [start] : []);
      return;
    }

    const existing = checkedNodes || [];

    const merged = checked
      ? [...existing, ...collected]
      : existing.filter(
          (n) =>
            !collected.some(
              (c) => keyOf(c) === keyOf(n) || getScenarioId(c) === getScenarioId(n),
            ),
        );

    const seen = new Set();
    const deduped = [];
    for (const n of merged) {
      const k = keyOf(n);
      if (k == null || seen.has(k)) continue;
      seen.add(k);
      deduped.push(n);
    }

    handleCheck?.(deduped);
  };

  const onRightClickEntity = (e, node) => {
    e.preventDefault();
    e.stopPropagation();
    // set setLastClickedEntity here if we want context menus based on entity
  };

const onSelect = (e, nodeIdRaw) => {
    const clickedInsideInput = e.target?.closest?.('input');
    const isEditing = Object.values(editingEntity).some(Boolean);

    // If user is editing and clicking inside the input, do nothing —
    // they're positioning their cursor, not changing selection.
    if (isEditing && clickedInsideInput) return;

    // Existing behavior: clicking outside the input while editing → stop edit
    if (isEditing && !clickedInsideInput) handleStopEdit();

    const nodeId = String(Array.isArray(nodeIdRaw) ? nodeIdRaw[0] : nodeIdRaw);
    const node = nodeIndex.get(nodeId);
    if (!node) return;

    if (node.scenario_id != null) {
        if (!highlightedScenarios?.map(Number).includes(Number(node.scenario_id))) {
            setHighlightedScenarios([node.scenario_id]);
        }
        setLastClickedEntity?.(null);
    } else if (node.entity_id != null) {
        const entityId = node.entity_id;
        if (lastClickedEntity && lastClickedEntity !== entityId) {
            setHighlightedScenarios([]);
        }
        setLastClickedEntity?.(Number(node.entity_id));
    }

    // Pass the node directly instead of just the key
    handleSelection?.([node.key], node);
    dispatch(entityBrowserActions.updateSelectedNodeIds([nodeId]));
};

  const findEntityById = (list, id) => {
    if (!Array.isArray(list)) return null;
    for (const ent of list) {
        if (Number(ent.entity_id) === Number(id)) return ent;
        const fromChildren = findEntityById(ent.children || [], id);
        if (fromChildren) return fromChildren;
    }
    return null;
};

  const onNewEntity = () => {
  // Prefer the last clicked entity, fall back to selected node
  const first = selectedNodeIds?.[0];
  const node = first ? nodeIndex.get(String(first)) : null;
  const entityId = lastClickedEntity ?? node?.entity_id ?? null;

  handleNewEntity?.(entityId);
};

  const onNewRootEntity = async () => {
    const root = await dispatch(createRootEntity('New Group'));
    if (root?.entity_id) {
      setLastClickedEntity(root.entity_id);
    }
  };

  const onNewScenario = () => {
    const first = selectedNodeIds?.[0];
    const node = first ? nodeIndex.get(String(first)) : null;
    const entityId = lastClickedEntity 
    ? Number(lastClickedEntity) 
    : node?.entity_id 
        ? Number(node.entity_id) 
        : null;
    if (!entityId) return;

    handleNewScenario?.(entityId);
  };

  const handleCheckboxChange = (checked, node) => {
    if (!handleCheck) return;
    const id = node.scenario_id ?? node.entity_id;

    let newChecked;
    if (radioButtonMode) {
      newChecked = checked ? [id] : [];
    } else {
      const set = new Set(checkedNodes);
      if (checked) set.add(id);
      else set.delete(id);
      newChecked = Array.from(set);
    }

    handleCheck(newChecked);
  };

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    const handleDragEnd = ({ active, over }) => {
      if (!over || active.id === over.id) return;

      const activeId = String(active.id);
      const overId = String(over.id);
      const kind = activeId.slice(0, 2);
      if (kind !== overId.slice(0, 2)) return;                      // no scenario↔entity

      const parentNodeId = parentIndex.get(activeId) ?? null;
      if (parentNodeId !== (parentIndex.get(overId) ?? null)) return; // same parent only


      const parentNode = parentNodeId ? nodeIndex.get(parentNodeId) : null;
      const siblings = ((parentNode ? parentNode.children : treeData) || []).filter((c) =>
        kind === 's:' ? c.isScenario : !c.isScenario,
      );

      const from = siblings.findIndex((c) => makeNodeId(c) === activeId);
      const to = siblings.findIndex((c) => makeNodeId(c) === overId);
      if (from < 0 || to < 0) return;

      const reordered = arrayMove(siblings, from, to);

      if (kind === 's:') {
        if (!parentNode?.entity_id) return;
        handleReorderScenarios?.(
          Number(parentNode.entity_id),
          reordered.map((c) => Number(c.scenario_id)),
        );
      } else {
        handleReorderEntities?.(
          parentNode ? Number(parentNode.entity_id) : null,
          reordered.map((c) => Number(c.entity_id)),
        );
      }
    };

  const renderOptions = (editableFlag) => {
    if (!editableFlag) return null;

    const hasEntityContext =
      (!!(selectedNodeIds?.length && nodeIndex.get(String(selectedNodeIds[0]))?.entity_id) ||
        !!lastClickedEntity);

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          color: 'white',
          margin: '4px 4px',
          marginTop: '20px',
          marginBottom: '25px',
        }}
      >
        <Space direction="vertical" size="small" align="center">
          <Button type="primary" onClick={onNewRootEntity}>
            New Group
          </Button>
          <Space>
            <Button type="primary" onClick={onNewEntity}>
              Add Unit
            </Button>
            <Button type="primary" disabled={!hasEntityContext} onClick={onNewScenario}>
              Add Scenario
            </Button>
          </Space>
        </Space>
      </div>
    );
  };
const collectEntityIdsWithChildren = (nodes, out = []) => {
  (nodes || []).forEach((n) => {
    // only expand entities that actually have children (entities or scenarios)
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;

    if (!n.scenario_id && hasChildren) {
      out.push(makeNodeId(n)); // e:<id>
      collectEntityIdsWithChildren(n.children, out);
    }
  });
  return out;
};

const entityIdsToExpand = useMemo(() => {
  return collectEntityIdsWithChildren(treeData, []);
}, [treeData]); 


 const renderExpandCollapseBtn = () => {
  const expanded =
    entityIdsToExpand.length > 0 &&
    entityIdsToExpand.every((id) => expandedNodes.includes(id));

  const expandOrCollapse = () => {
    dispatch(
      entityBrowserActions.updateExpandedNodeIds(expanded ? [] : entityIdsToExpand)
    );
  };

       
    return (
      <div>
        <Button
          style={{
            minWidth: 0,
            width: '25px',
            height: '25px',
            float: 'right',
            marginBottom: 10,
            marginRight: 10,
            backgroundColor: '#CCCCCC',
          }}
          shape="circle"
          icon={
            expanded ? (
              <MenuUnfoldOutlined style={{ fontSize: 13 }} />
            ) : (
              <MenuFoldOutlined style={{ fontSize: 13 }} />
            )
          }
          onClick={expandOrCollapse}
        />
      </div>
    );
  };
  
  const renderTreeItem = (node, nodeId, dnd) => {
  const isScenario = !!node.isScenario;
  const isHighlightedScenario =
  isScenario && highlightedScenarios?.includes(node.scenario_id);
  const isEditing = !!editingEntity[nodeId];
  const isSelected = selectedNodeIds?.[0] === nodeId;
  const isActive = isHighlightedScenario || isSelected;

  const isChecked = isScenario
    ? checkedNodes.includes(node.scenario_id)
    : false;

  const textColor = isEditing
  ? 'black'
  : isActive
    ? '#fff'
    : isScenario
      ? '#1890ff'
      : editable
        ? '#CCCCCC'
        : 'black';
    
    return (
      <TreeItem
        key={nodeId}
        nodeId={nodeId}
        ref={dnd.setNodeRef}
        style={dnd.style}
        sx={
          isScenario
            ? {
                 '&&& .MuiTreeItem-content.Mui-selected, &&& .MuiTreeItem-content.Mui-selected:hover': {
          backgroundColor: '#1890ff',
          color: '#fff',
        },
      }
    : undefined
        }
        onContextMenu={(e) => onRightClickEntity(e, node)}
        collapseIcon={
          Array.isArray(node.children) && node.children.length ? <ExpandMoreIcon sx={{ color: '#fff' }}/> : null
        }
        expandIcon={
          Array.isArray(node.children) && node.children.length ? <ChevronRightIcon sx={{ color: '#fff' }} /> : null
        }
        label={
          <Stack direction="row" justifyContent="start" alignItems="center" spacing={0}>
            {/* Show checkboxes only in non-editable mode and only for scenarios */}

             <IconButton
                aria-label="reorder"
                size="small"
                disableRipple
                {...dnd.handleProps}
                onMouseDown={stopPropagation}
                sx={{
                  cursor: 'grab',
                  paddingBottom: '6px',
                  display: !editable || !reorderable ? 'none' : '',
                  '&:active': { cursor: 'grabbing' },
                }}
              >
              <DragHandleIcon sx={{ fontSize: 15, color: '#CCCCCC' }} />
            </IconButton>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => {
                    stopPropagation(e);
                    handleCheckboxChange(e.target.checked, node);
                  }}
                />
              }
              sx={{
                display: !editable && isScenario ? '' : 'none',
                marginRight: 0,
              }}
            />

            <Input
              value={isEditing ? editingName : node.name}
              readOnly={!isEditing}
              disableUnderline
              onChange={(e) => {
                if (isEditing) {
                  setEditingName(e.target.value);
                }
              }}
              onBlur={() => {
                if (isEditing) {
                  handleStopEdit(nodeId);
                }
              }}
              onKeyDown={(e) => handleEnterPress(e, node, nodeId)}
              sx={{
                color: editable ? (isEditing ? 'black' : '#CCCCCC') : 'black',
                bgcolor: isEditing ? 'white' : '',
                height: '100%',
                fontSize: 17,
                fontWeight: 'inherit',
                paddingBottom: '2px',
                input: { cursor: 'pointer' },

                color: textColor,

                // ensure actual <input> gets it too
                '& input': {
                  color: textColor,
                  cursor: isEditing ? 'text' : 'pointer',
                },
                
                ...(isScenario && {
                  // fontFamily: 'cursive', 
                  // fontSize: 15,  // or add font 
                   fontStyle: 'italic',                
                  // letterSpacing: '0.2px',             
                  }),

                
              }}
            />

            {isEditing && (
              <span style={{ fontSize: 10, color: '#f8f5f5ff', marginLeft: 4 }}>
                Enter = save, Esc = cancel
              </span>
            )}

            <IconButton
              aria-label="edit"
              size="small"
              onClick={(e) => {
                stopPropagation(e);
                handleStartEdit(nodeId, node.name);
              }}
              sx={{ paddingBottom: '6px', display: !editable ? 'none' : '' }}
            >
              <EditIcon sx={{ fontSize: 17, color: '#CCCCCC' }} />
            </IconButton>

            <IconButton
              disabled={!editable}
              variant="contained"
              aria-label="delete"
              size="small"
              onClick={(e) => {
                stopPropagation(e);
                setDeleteNode(node);
                handleOpenDeletePopup(e);
              }}
              sx={{ paddingBottom: '6px', display: !editable ? 'none' : '' }}
            >
              <DeleteIcon sx={{ fontSize: 17, color: '#CCCCCC' }} />
            </IconButton>

            <Popover
              open={openDelPopOver}
              anchorEl={delAnchorEl}
              onClose={handleCloseDeletePopup}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: 'right' }}
            >
              <Stack direction="column" justifyContent="center" alignItems="center" spacing={0}>
                <MatButton
                  onClick={(e) => {
                    stopPropagation(e);
                    if (handleDelete && deleteNode) handleDelete(deleteNode);
                    handleCloseDeletePopup(e);
                  }}
                  sx={{ width: '12.5vh', color: 'red', marginTop: '10px' }}
                >
                  Delete
                </MatButton>
                <MatButton
                  onClick={(e) => {
                    stopPropagation(e);
                    e.preventDefault();
                    handleCloseDeletePopup(e);
                  }}
                  sx={{ width: '12.5vh', marginBottom: '10px' }}
                >
                  Cancel
                </MatButton>
              </Stack>
            </Popover>
          </Stack>
        }
      >
        
        {(() => {
          const kids = Array.isArray(node.children) ? node.children : [];
          const scenarios = kids.filter((c) => c.isScenario);
          const entities = kids.filter((c) => !c.isScenario);
          return (
            <>
              <SortableContext items={scenarios.map(makeNodeId)} strategy={verticalListSortingStrategy}>
                {scenarios.map((c) => renderTree(c))}
              </SortableContext>
              <SortableContext items={entities.map(makeNodeId)} strategy={verticalListSortingStrategy}>
                {entities.map((c) => renderTree(c))}
              </SortableContext>
            </>
          );
        })()}

      </TreeItem>
    );
  };

  const renderTree = (node) => {
  const nodeId = makeNodeId(node);
  return (
    <SortableNode key={nodeId} nodeId={nodeId} disabled={!editable || !reorderable }>
      {(dnd) => renderTreeItem(node, nodeId, dnd)}
    </SortableNode>
  );
};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {renderOptions(editable)}
      {editable ? renderExpandCollapseBtn() : null}
      <ThemeProvider theme={fontMuiTreeView}>
         <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
        
        <TreeView
          aria-label="rich object"
          key={String(entitiesVersion)}
          expanded={editable ? expandedNodes : expandedBusinessCases}
          onNodeToggle={handleToggle}
          onNodeSelect={onSelect}
          selected={selectedNodeIds?.[0] ?? ''}
          defaultSelected={[]}
          sx={{
            height: !editable ? '40vh' : '100vh',
            overflowY: 'scroll',
          }}
        >
          {treeData && treeData.length > 0 ? (
            <SortableContext items={treeData.map(makeNodeId)} strategy={verticalListSortingStrategy}>
                {treeData.map((node) => renderTree(node))}
              </SortableContext>
            ) : (
            <span style={{ color: '#CCCCCC' }}>
              Create a Unit, and then create an underlying scenario to get started.
            </span>
          )}
        </TreeView>
        </DndContext>
      </ThemeProvider>
    </div>
  );
};


export default EntitiesBrowser;
